import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { supabase } from '../lib/supabase'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const db = (t: string): any => (supabase as any).from(t)

export type EmploymentStatus =
  | 'active'
  | 'suspended'
  | 'terminated'
  | 'retired'
  | 'resigned'

export type GradeTypeLite = 'fixed' | 'range'

export interface StaffListItem {
  id: string
  personnel_number: number
  full_name: string
  first_name: string
  middle_name: string | null
  last_name: string
  photo_url: string | null
  date_of_birth: string | null
  gender: string | null
  phone: string | null
  email: string | null
  job_title: string | null
  grade_id: string | null
  grade_name: string | null
  grade_type: GradeTypeLite | null
  position_in_range_percent: number | null
  schedule_id: string | null
  schedule_name: string | null
  location_id: string | null
  location_name: string | null
  department: string | null
  employment_status: EmploymentStatus
  date_of_employment: string
  nhf_enrolled: boolean
  residential_address: string | null
  bank_name: string | null
  bank_account_number: string | null
  emergency_contact_name: string | null
  emergency_contact_relationship: string | null
  emergency_contact_phone: string | null
  created_at: string
  updated_at: string
}

type StaffRow = {
  id: string
  personnel_number: number
  full_name: string
  first_name: string
  middle_name: string | null
  last_name: string
  email: string | null
  phone: string | null
  gender: string | null
  date_of_birth: string | null
  employment_status: EmploymentStatus
  date_of_employment: string
  department: string | null
  job_title_other: string | null
  salary_grade_id: string
  position_in_range_percent: string | number | null
  working_schedule_id: string
  location_id: string | null
  job_title_id: string | null
  nhf_enrolled: boolean
  residential_address: string | null
  bank_name: string | null
  bank_account_number: string | null
  emergency_contact_name: string | null
  emergency_contact_relationship: string | null
  emergency_contact_phone: string | null
  created_at: string
  updated_at: string
  salary_grades: { name: string; grade_type: GradeTypeLite } | null
  working_schedules: { name: string } | null
  warehouses: { name: string } | null
  job_titles: { title: string } | null
}

const numOrNull = (v: string | number | null | undefined): number | null =>
  v === null || v === undefined ? null : typeof v === 'number' ? v : parseFloat(v)

export function useStaff(tenantId: string) {
  return useQuery<StaffListItem[]>({
    queryKey: ['staff', tenantId],
    enabled: !!tenantId,
    queryFn: async () => {
      const { data, error } = await db('staff')
        .select(
          `id, personnel_number, full_name, first_name, middle_name, last_name,
           email, phone, gender, date_of_birth,
           employment_status, date_of_employment, department, job_title_other,
           salary_grade_id, position_in_range_percent, working_schedule_id,
           location_id, job_title_id,
           nhf_enrolled, residential_address,
           bank_name, bank_account_number,
           emergency_contact_name, emergency_contact_relationship,
           emergency_contact_phone,
           created_at, updated_at,
           salary_grades ( name, grade_type ),
           working_schedules ( name ),
           warehouses ( name ),
           job_titles ( title )`,
        )
        .eq('tenant_id', tenantId)
        .order('full_name', { ascending: true })

      if (error) throw error

      return (data as StaffRow[] ?? []).map(r => ({
        id: r.id,
        personnel_number: r.personnel_number,
        full_name: r.full_name,
        first_name: r.first_name,
        middle_name: r.middle_name,
        last_name: r.last_name,
        photo_url: null,
        date_of_birth: r.date_of_birth,
        gender: r.gender,
        phone: r.phone,
        email: r.email,
        job_title: r.job_titles?.title ?? r.job_title_other ?? null,
        grade_id: r.salary_grade_id,
        grade_name: r.salary_grades?.name ?? null,
        grade_type: r.salary_grades?.grade_type ?? null,
        position_in_range_percent: numOrNull(r.position_in_range_percent),
        schedule_id: r.working_schedule_id,
        schedule_name: r.working_schedules?.name ?? null,
        location_id: r.location_id,
        location_name: r.warehouses?.name ?? null,
        department: r.department,
        employment_status: r.employment_status,
        date_of_employment: r.date_of_employment,
        nhf_enrolled: r.nhf_enrolled,
        residential_address: r.residential_address,
        bank_name: r.bank_name,
        bank_account_number: r.bank_account_number,
        emergency_contact_name: r.emergency_contact_name,
        emergency_contact_relationship: r.emergency_contact_relationship,
        emergency_contact_phone: r.emergency_contact_phone,
        created_at: r.created_at,
        updated_at: r.updated_at,
      }))
    },
  })
}

export interface LocationOption {
  id: string
  name: string
}

export function useLocations(tenantId: string) {
  return useQuery<LocationOption[]>({
    queryKey: ['warehouses-for-staff-filter', tenantId],
    enabled: !!tenantId,
    queryFn: async () => {
      const { data, error } = await db('warehouses')
        .select('id, name')
        .eq('tenant_id', tenantId)
        .eq('is_active', true)
        .order('name', { ascending: true })

      if (error) throw error
      return (data ?? []) as LocationOption[]
    },
  })
}

export type Gender = 'male' | 'female' | 'prefer_not_to_say'

export interface CreateStaffInput {
  // Personal
  first_name: string
  middle_name: string | null
  last_name: string
  date_of_birth: string | null
  gender: Gender | null
  phone: string | null
  email: string | null

  // Employment
  job_title_id: string | null
  job_title_other: string | null
  tenant_job_title_id: string | null
  department: string | null
  location_id: string
  date_of_employment: string

  // Compensation
  salary_grade_id: string
  position_in_range_percent: number | null
  working_schedule_id: string
  nhf_enrolled: boolean

  // Tax relief (NTA 2025)
  annual_rent: number
  rent_relief_eligible: boolean
  rent_relief_note: string | null

  // Next of Kin (all nullable)
  emergency_contact_name: string | null
  emergency_contact_phone: string | null
  emergency_contact_relationship: string | null
}

export interface CreateStaffResult {
  id: string
  personnel_number: number
  full_name: string
}

async function fetchNextPersonnelNumber(tenantId: string): Promise<number> {
  const { data, error } = await db('tax_settings')
    .select('personnel_number_next')
    .eq('tenant_id', tenantId)
    .maybeSingle()
  if (error) throw error
  if (!data) throw new Error('Tax settings not found for this tenant')
  return (data as { personnel_number_next: number }).personnel_number_next
}

async function bumpPersonnelNumber(tenantId: string, current: number): Promise<void> {
  const { error } = await db('tax_settings')
    .update({ personnel_number_next: current + 1 })
    .eq('tenant_id', tenantId)
    .eq('personnel_number_next', current)
  if (error) throw error
}

export interface StaffDetail extends StaffListItem {
  user_id: string | null

  // Names + identity
  job_title_id: string | null
  job_title_other: string | null
  tenant_job_title_id: string | null
  position_description: string | null

  // Address
  residential_city: string | null
  residential_state: string | null
  residential_country: string | null

  // Employment
  date_of_separation: string | null
  notes: string | null
  employee_group: string | null
  employee_subgroup: string | null
  division: string | null
  unit: string | null

  // Banking
  bank_account_name: string | null

  // Statutory identifiers
  national_id: string | null
  tin: string | null
  pension_pin: string | null
  pension_fund_administrator: string | null
  nhf_number: string | null

  // Tax relief (NTA 2025) — read by post_payroll_atomic for 2026+ runs
  annual_rent: number
  rent_relief_eligible: boolean
  rent_relief_note: string | null

  // Housing
  company_house: boolean
}

type StaffDetailRow = {
  id: string
  tenant_id: string
  user_id: string | null
  personnel_number: number
  full_name: string
  first_name: string
  middle_name: string | null
  last_name: string
  email: string | null
  phone: string | null
  gender: string | null
  date_of_birth: string | null
  date_of_employment: string
  date_of_separation: string | null
  employment_status: EmploymentStatus
  employee_group: string | null
  employee_subgroup: string | null
  location_id: string | null
  division: string | null
  unit: string | null
  department: string | null
  job_title_id: string | null
  job_title_other: string | null
  tenant_job_title_id: string | null
  position_description: string | null
  salary_grade_id: string
  position_in_range_percent: string | number | null
  working_schedule_id: string
  company_house: boolean
  residential_address: string | null
  residential_city: string | null
  residential_state: string | null
  residential_country: string | null
  bank_name: string | null
  bank_account_name: string | null
  bank_account_number: string | null
  national_id: string | null
  tin: string | null
  pension_pin: string | null
  pension_fund_administrator: string | null
  nhf_number: string | null
  nhf_enrolled: boolean
  annual_rent: string | number | null
  rent_relief_eligible: boolean
  rent_relief_note: string | null
  emergency_contact_name: string | null
  emergency_contact_phone: string | null
  emergency_contact_relationship: string | null
  notes: string | null
  created_at: string
  updated_at: string
  salary_grades: { name: string; grade_type: GradeTypeLite } | null
  working_schedules: { name: string } | null
  warehouses: { name: string } | null
  job_titles: { title: string } | null
}

const STAFF_DETAIL_SELECT = `
  id, tenant_id, user_id, personnel_number,
  full_name, first_name, middle_name, last_name,
  email, phone, gender, date_of_birth,
  date_of_employment, date_of_separation, employment_status,
  employee_group, employee_subgroup,
  location_id, division, unit, department,
  job_title_id, job_title_other, tenant_job_title_id, position_description,
  salary_grade_id, position_in_range_percent, working_schedule_id,
  company_house,
  residential_address, residential_city, residential_state, residential_country,
  bank_name, bank_account_name, bank_account_number,
  national_id, tin, pension_pin, pension_fund_administrator,
  nhf_number, nhf_enrolled,
  annual_rent, rent_relief_eligible, rent_relief_note,
  emergency_contact_name, emergency_contact_phone, emergency_contact_relationship,
  notes, created_at, updated_at,
  salary_grades ( name, grade_type ),
  working_schedules ( name ),
  warehouses ( name ),
  job_titles ( title )
`

function rowToStaffDetail(r: StaffDetailRow): StaffDetail {
  return {
    id: r.id,
    personnel_number: r.personnel_number,
    full_name: r.full_name,
    first_name: r.first_name,
    middle_name: r.middle_name,
    last_name: r.last_name,
    photo_url: null,
    date_of_birth: r.date_of_birth,
    gender: r.gender,
    phone: r.phone,
    email: r.email,
    job_title: r.job_titles?.title ?? r.job_title_other ?? null,
    grade_id: r.salary_grade_id,
    grade_name: r.salary_grades?.name ?? null,
    grade_type: r.salary_grades?.grade_type ?? null,
    position_in_range_percent: numOrNull(r.position_in_range_percent),
    schedule_id: r.working_schedule_id,
    schedule_name: r.working_schedules?.name ?? null,
    location_id: r.location_id,
    location_name: r.warehouses?.name ?? null,
    department: r.department,
    employment_status: r.employment_status,
    date_of_employment: r.date_of_employment,
    nhf_enrolled: r.nhf_enrolled,
    residential_address: r.residential_address,
    bank_name: r.bank_name,
    bank_account_number: r.bank_account_number,
    emergency_contact_name: r.emergency_contact_name,
    emergency_contact_relationship: r.emergency_contact_relationship,
    emergency_contact_phone: r.emergency_contact_phone,
    created_at: r.created_at,
    updated_at: r.updated_at,

    user_id: r.user_id,
    job_title_id: r.job_title_id,
    tenant_job_title_id: r.tenant_job_title_id,
    job_title_other: r.job_title_other,
    position_description: r.position_description,
    residential_city: r.residential_city,
    residential_state: r.residential_state,
    residential_country: r.residential_country,
    date_of_separation: r.date_of_separation,
    notes: r.notes,
    employee_group: r.employee_group,
    employee_subgroup: r.employee_subgroup,
    division: r.division,
    unit: r.unit,
    bank_account_name: r.bank_account_name,
    national_id: r.national_id,
    tin: r.tin,
    pension_pin: r.pension_pin,
    pension_fund_administrator: r.pension_fund_administrator,
    nhf_number: r.nhf_number,
    annual_rent: numOrNull(r.annual_rent) ?? 0,
    rent_relief_eligible: r.rent_relief_eligible,
    rent_relief_note: r.rent_relief_note,
    company_house: r.company_house,
  }
}

export function useStaffById(staffId: string) {
  return useQuery<StaffDetail | null>({
    queryKey: ['staff-detail', staffId],
    enabled: !!staffId,
    queryFn: async () => {
      const { data, error } = await db('staff')
        .select(STAFF_DETAIL_SELECT)
        .eq('id', staffId)
        .maybeSingle()
      if (error) throw error
      if (!data) return null
      return rowToStaffDetail(data as StaffDetailRow)
    },
  })
}

export type StaffUpdateFields = Partial<{
  // Profile
  first_name: string
  middle_name: string | null
  last_name: string
  date_of_birth: string | null
  gender: Gender | null
  phone: string | null
  email: string | null
  residential_address: string | null
  residential_city: string | null
  residential_state: string | null
  residential_country: string | null
  emergency_contact_name: string | null
  emergency_contact_phone: string | null
  emergency_contact_relationship: string | null

  // Employment
  date_of_employment: string
  date_of_separation: string | null
  employment_status: EmploymentStatus
  notes: string | null
  job_title_id: string | null
  job_title_other: string | null
  tenant_job_title_id: string | null
  department: string | null
  location_id: string | null
  working_schedule_id: string
  employee_group: string | null
  employee_subgroup: string | null

  // Compensation
  salary_grade_id: string
  position_in_range_percent: number | null
  nhf_enrolled: boolean
  pension_fund_administrator: string | null
  pension_pin: string | null
  tin: string | null

  // Tax relief (NTA 2025)
  annual_rent: number
  rent_relief_eligible: boolean
  rent_relief_note: string | null

  // Banking
  bank_name: string | null
  bank_account_name: string | null
  bank_account_number: string | null
  national_id: string | null
}>

export function useUpdateStaff(tenantId: string) {
  const qc = useQueryClient()
  return useMutation<StaffDetail, Error, { id: string; fields: StaffUpdateFields }>({
    mutationFn: async ({ id, fields }) => {
      if (!tenantId) throw new Error('Tenant not loaded')
      if (!id) throw new Error('Staff id missing')

      try {
        const { data, error } = await db('staff')
          .update(fields)
          .eq('id', id)
          .eq('tenant_id', tenantId)
          .select(STAFF_DETAIL_SELECT)
          .single()
        if (error) throw error
        return rowToStaffDetail(data as StaffDetailRow)
      } catch (err) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const e = err as any
        const code = e?.code
        const msg = (e?.message ?? '').toLowerCase()
        if (
          code === '23503' ||
          msg.includes('foreign key') ||
          msg.includes('violates')
        ) {
          throw new Error('Could not save: a related record (grade, schedule, location, or job title) is invalid.')
        }
        if (code === '23514' || msg.includes('check constraint')) {
          throw new Error('Could not save: one or more values are out of range.')
        }
        throw err
      }
    },
    onSuccess: (detail) => {
      qc.setQueryData(['staff-detail', detail.id], detail)
      qc.invalidateQueries({ queryKey: ['staff', tenantId] })
      qc.invalidateQueries({ queryKey: ['staff-count-by-grade', tenantId] })
      qc.invalidateQueries({ queryKey: ['staff-count-by-schedule', tenantId] })
    },
  })
}

export function useCreateStaff(tenantId: string) {
  const qc = useQueryClient()
  return useMutation<CreateStaffResult, Error, CreateStaffInput>({
    mutationFn: async (input) => {
      if (!tenantId) throw new Error('Tenant not loaded')

      const attempt = async (personnelNumber: number): Promise<CreateStaffResult> => {
        const payload = {
          tenant_id: tenantId,
          personnel_number: personnelNumber,
          first_name: input.first_name.trim(),
          middle_name: input.middle_name?.trim() || null,
          last_name: input.last_name.trim(),
          email: input.email?.trim() || null,
          phone: input.phone?.trim() || null,
          gender: input.gender,
          date_of_birth: input.date_of_birth,
          date_of_employment: input.date_of_employment,
          employment_status: 'active' as const,
          department: input.department?.trim() || null,
          location_id: input.location_id,
          job_title_id: input.job_title_id,
          job_title_other: input.job_title_other?.trim() || null,
          tenant_job_title_id: input.tenant_job_title_id ?? null,
          salary_grade_id: input.salary_grade_id,
          position_in_range_percent: input.position_in_range_percent,
          working_schedule_id: input.working_schedule_id,
          nhf_enrolled: input.nhf_enrolled,
          annual_rent: input.annual_rent,
          rent_relief_eligible: input.rent_relief_eligible,
          rent_relief_note: input.rent_relief_note?.trim() || null,
          emergency_contact_name: input.emergency_contact_name?.trim() || null,
          emergency_contact_phone: input.emergency_contact_phone?.trim() || null,
          emergency_contact_relationship:
            input.emergency_contact_relationship?.trim() || null,
        }

        const { data, error } = await db('staff')
          .insert(payload)
          .select('id, personnel_number, full_name')
          .single()
        if (error) throw error
        return data as CreateStaffResult
      }

      let next = await fetchNextPersonnelNumber(tenantId)
      let result: CreateStaffResult
      try {
        result = await attempt(next)
      } catch (err) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const e = err as any
        const code = e?.code
        const msg = (e?.message ?? '').toLowerCase()
        const isDuplicate =
          code === '23505' ||
          msg.includes('duplicate') ||
          msg.includes('unique')
        if (!isDuplicate) throw err
        // Race condition: another row took this number. Re-read and retry once.
        next = await fetchNextPersonnelNumber(tenantId)
        result = await attempt(next)
      }

      await bumpPersonnelNumber(tenantId, next)
      return result
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['staff', tenantId] })
      qc.invalidateQueries({ queryKey: ['staff-count-by-grade', tenantId] })
      qc.invalidateQueries({ queryKey: ['staff-count-by-schedule', tenantId] })
      qc.invalidateQueries({ queryKey: ['payroll-tax-settings', tenantId] })
    },
  })
}
